/**
 * User info from /auth/me (HttpOnly cookie auth).
 * Cached in memory; use clearUserDataCache() on logout.
 */

import { getBaseUrl, getCsrfToken } from './apiUtils';

export interface UserMe {
  id: string;
  username?: string;
  name?: string;
  title?: string;
  group?: { name?: string } | null;
  groupName?: string;
  groupId?: string;
  permissions?: unknown;
  email?: string;
  lastLogin?: string;
  createdAt?: string;
  functions?: any[];
  Functions?: any[];
}

let userCache: UserMe | null = null;

/**
 * Fetch current user from /auth/me. Uses credentials (HttpOnly cookies) + CSRF.
 * Caches result; returns null on 401/403 or missing data.
 */
export async function getUserData(baseUrl?: string): Promise<UserMe | null> {
  const url = baseUrl || getBaseUrl();
  if (!url) return null;
  try {
    const csrfToken = await getCsrfToken(url);
    const response = await fetch(`${url}/auth/me`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-CSRF-TOKEN': csrfToken,
      },
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        userCache = null;
        return null;
      }
      return null;
    }
    const json = await response.json();
    const data = json?.data;
    if (data?.id) {
      userCache = data as UserMe;
      return userCache;
    }
    userCache = null;
    return null;
  } catch {
    return null;
  }
}

/**
 * Current user id from cache (sync). May be null if not yet loaded.
 */
export function getUserId(): string | null {
  return userCache?.id ?? null;
}

/**
 * Current user role (groupName) from cache (sync). May be null if not yet loaded.
 */
export function getUserRole(): string | null {
  return userCache?.groupName ?? null;
}

/**
 * Clear cached user data. Call on logout.
 */
export function clearUserDataCache(): void {
  userCache = null;
}
